import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';
import ParrotAssignmentModal from './ParrotAssignmentModal.jsx';
import KnowledgeBaseSyncStatusPanel from '../KnowledgeBaseSyncStatusPanel.jsx';

/**
 * Parrot details panel for viewing and editing parrot configuration
 */
const ParrotDetailsPanel = ({
  parrot,
  onUpdate,
  onViewRevisions,
  onClose
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [parrotDetails, setParrotDetails] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    system_prompt: '',
    first_message: '',
    voice_id: '',
    voice_settings: {
      stability: 0.5,
      similarity_boost: 0.5,
      style: 0.0,
      use_speaker_boost: true
    },
    status: 'active',
    user_id: '',
    phone_numbers: []
  });

  /**
   * Fetch available phone numbers for assignment
   */
  const fetchAvailablePhoneNumbers = async () => {
    try {
      setLoadingPhoneNumbers(true);
      const token = await getToken();
      
      const response = await apiRequest('/api/admin/phone-numbers/all', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setAvailablePhoneNumbers(data.phoneNumbers || []);

    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      showToast(error.message || 'Failed to load phone numbers', 'error');
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  /**
   * Fetch detailed parrot information
   */
  const fetchParrotDetails = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/parrots/${parrot.id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const details = await response.json();
      setParrotDetails(details);
      
      // Initialize edit form with current values
      setEditForm({
        name: details.name || '',
        system_prompt: details.configuration?.system_prompt || '',
        first_message: details.configuration?.first_message || '',
        voice_id: details.configuration?.voice_id || '',
        voice_settings: {
          stability: details.configuration?.voice_settings?.stability || 0.5,
          similarity_boost: details.configuration?.voice_settings?.similarity_boost || 0.5,
          style: details.configuration?.voice_settings?.style || 0.0,
          use_speaker_boost: details.configuration?.voice_settings?.use_speaker_boost !== undefined ? details.configuration.voice_settings.use_speaker_boost : true
        },
        status: details.status || 'active',
        user_id: details.user?.id || '',
        phone_numbers: details.phone_numbers?.map(phone => phone.id) || []
      });

    } catch (error) {
      console.error('Error fetching parrot details:', error);
      showToast(error.message || 'Failed to load parrot details', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field, value) => {
    if (field.startsWith('voice_settings.')) {
      const voiceField = field.split('.')[1];
      setEditForm(prev => ({
        ...prev,
        voice_settings: {
          ...prev.voice_settings,
          [voiceField]: value
        }
      }));
    } else {
      setEditForm(prev => ({ ...prev, [field]: value }));
    }
  };

  /**
   * Handle phone number assignment changes
   */
  const handlePhoneNumberChange = (phoneNumberId, isAssigned) => {
    setEditForm(prev => ({
      ...prev,
      phone_numbers: isAssigned 
        ? [...prev.phone_numbers, phoneNumberId]
        : prev.phone_numbers.filter(id => id !== phoneNumberId)
    }));
  };

  /**
   * Save parrot changes
   */
  const handleSave = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/parrots/${parrot.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const updatedParrot = await response.json();
      setParrotDetails(updatedParrot);
      setEditing(false);
      onUpdate(updatedParrot);
      showToast('Parrot updated successfully', 'success');

    } catch (error) {
      console.error('Error updating parrot:', error);
      showToast(error.message || 'Failed to update parrot', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle parrot deletion
   */
  const handleDeleteParrot = async () => {
    try {
      setDeleting(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/parrots/${parrot.id}/hard-delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const result = await response.json();
      showToast('Parrot permanently deleted', 'success');
      setShowDeleteModal(false);
      onUpdate(null); // Signal that parrot was deleted
      onClose(); // Close the details panel

    } catch (error) {
      console.error('Error deleting parrot:', error);
      showToast(error.message || 'Failed to delete parrot', 'error');
    } finally {
      setDeleting(false);
    }
  };

  
  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status) => {
    const baseClasses = 'px-3 py-1 rounded-full text-sm font-medium';
    
    switch (status?.toLowerCase()) {
      case 'active':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
            : 'bg-green-100 text-green-800 border border-green-200'
        }`;
      case 'inactive':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-red-900/30 text-red-400 border border-red-500/30' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`;
      case 'draft':
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30' 
            : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
        }`;
      default:
        return `${baseClasses} ${
          theme === 'dark' 
            ? 'bg-gray-900/30 text-gray-400 border border-gray-500/30' 
            : 'bg-gray-100 text-gray-800 border border-gray-200'
        }`;
    }
  };

  // Load parrot details when component mounts or parrot changes
  useEffect(() => {
    if (parrot?.id) {
      fetchParrotDetails();
    }
  }, [parrot?.id]);

  // Load phone numbers when editing starts
  useEffect(() => {
    if (editing) {
      fetchAvailablePhoneNumbers();
    }
  }, [editing]);

  if (loading && !parrotDetails) {
    return (
      <div className={"bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm"} data-theme={theme}>
        <div className="animate-pulse">
          <div className={`h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded w-1/4 mb-4`}></div>
          <div className="space-y-3">
            <div className={`h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded`}></div>
            <div className={`h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded w-3/4`}></div>
            <div className={`h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded w-1/2`}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={"bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"} data-theme={theme}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
        <h3 className={"text-lg font-medium text-gray-900 dark:text-white"}>Parrot Details</h3>
        <div className="flex items-center space-x-2">
          {!editing ? (
            <>
              <button
                onClick={() => setEditing(true)}
                className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
              >
                Edit
              </button>
              <button
                onClick={() => onViewRevisions(parrot)}
                className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors dark:text-white"
              >
                View History
              </button>
              <button
                onClick={() => setShowAssignmentModal(true)}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors dark:text-white"
              >
                Assign User
              </button>
                <button
                onClick={() => setShowDeleteModal(true)}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center space-x-1 dark:text-white"
              >
                <span>🗑️</span>
                <span>Delete</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors disabled:opacity-50 dark:text-white"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  // Reset form to original values
                  setEditForm({
                    name: parrotDetails?.name || '',
                    system_prompt: parrotDetails?.configuration?.system_prompt || '',
                    first_message: parrotDetails?.configuration?.first_message || '',
                    voice_id: parrotDetails?.configuration?.voice_id || '',
                    voice_settings: {
                      stability: parrotDetails?.configuration?.voice_settings?.stability || 0.5,
                      similarity_boost: parrotDetails?.configuration?.voice_settings?.similarity_boost || 0.5,
                      style: parrotDetails?.configuration?.voice_settings?.style || 0.0,
                      use_speaker_boost: parrotDetails?.configuration?.voice_settings?.use_speaker_boost !== undefined ? parrotDetails.configuration.voice_settings.use_speaker_boost : true
                    },
                    status: parrotDetails?.status || 'active',
                    user_id: parrotDetails?.user?.id || '',
                    phone_numbers: parrotDetails?.phone_numbers?.map(phone => phone.id) || []
                  });
                }}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors dark:text-white"
              >
                Cancel
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className={`${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h4 className={"text-md font-medium text-gray-900 dark:text-white mb-4"}>Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Name
              </label>
              {editing ? (
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white'
                  } border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                />
              ) : (
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{parrotDetails?.name || 'Unnamed Parrot'}</p>
              )}
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Status
              </label>
              {editing ? (
                <select
                  value={editForm.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className={`w-full px-3 py-2 ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="draft">Draft</option>
                </select>
              ) : (
                <span className={getStatusBadge(parrotDetails?.status)}>
                  {parrotDetails?.status || 'Active'}
                </span>
              )}
            </div>

            <div className="md:col-span-2">
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Owner
              </label>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {parrotDetails?.user?.name || parrotDetails?.user?.email || 'Unassigned'}
                  </p>
                  {parrotDetails?.user?.email && (
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{parrotDetails.user.email}</p>
                  )}
                </div>
                <button
                  onClick={() => setShowAssignmentModal(true)}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors dark:text-white"
                >
                  Change
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Prompt Configuration */}
        <div>
          <h4 className={`text-md font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Prompt Configuration</h4>
          <div className="space-y-4">
            {/* First Message */}
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                First Message
              </label>
              {editing ? (
                <textarea
                  value={editForm.first_message || ''}
                  onChange={(e) => handleInputChange('first_message', e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  placeholder="Enter the first message the parrot will say when answering calls..."
                />
              ) : (
                <div className={`${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-700'} rounded-md p-3 whitespace-pre-wrap`}>
                  {parrotDetails?.configuration?.first_message || 'No first message configured'}
                </div>
              )}
            </div>

            {/* System Prompt */}
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                System Prompt
              </label>
              {editing ? (
                <textarea
                  value={editForm.system_prompt}
                  onChange={(e) => handleInputChange('system_prompt', e.target.value)}
                  rows={6}
                  className={`w-full px-3 py-2 ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  placeholder="Enter the system prompt for this parrot..."
                />
              ) : (
                <div className={`${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-700'} rounded-md p-3 whitespace-pre-wrap`}>
                  {parrotDetails?.configuration?.system_prompt || 'No prompt configured'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Voice Settings */}
        <div>
          <h4 className={`text-md font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Voice Settings</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Voice ID
              </label>
              {editing ? (
                <input
                  type="text"
                  value={editForm.voice_id}
                  onChange={(e) => handleInputChange('voice_id', e.target.value)}
                  className={`w-full px-3 py-2 ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                  placeholder="ElevenLabs Voice ID"
                />
              ) : (
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-mono text-sm`}>
                  {parrotDetails?.configuration?.voice_id || 'Not configured'}
                </p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Speaker Boost
              </label>
              {editing ? (
                <select
                  value={editForm.voice_settings.use_speaker_boost}
                  onChange={(e) => handleInputChange('voice_settings.use_speaker_boost', e.target.value === 'true')}
                  className={`w-full px-3 py-2 ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500`}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              ) : (
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {parrotDetails?.configuration?.voice_settings?.use_speaker_boost ? 'Enabled' : 'Disabled'}
                </p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Stability: {editing ? editForm.voice_settings.stability : parrotDetails?.configuration?.voice_settings?.stability || 0.5}
              </label>
              {editing ? (
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={editForm.voice_settings.stability}
                  onChange={(e) => handleInputChange('voice_settings.stability', parseFloat(e.target.value))}
                  className="w-full"
                />
              ) : (
                <div className={`w-full ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded-full h-2`}>
                  <div 
                    className="bg-teal-600 h-2 rounded-full" 
                    style={{ width: `${(parrotDetails?.configuration?.voice_settings?.stability || 0.5) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Similarity Boost: {editing ? editForm.voice_settings.similarity_boost : parrotDetails?.configuration?.voice_settings?.similarity_boost || 0.5}
              </label>
              {editing ? (
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={editForm.voice_settings.similarity_boost}
                  onChange={(e) => handleInputChange('voice_settings.similarity_boost', parseFloat(e.target.value))}
                  className="w-full"
                />
              ) : (
                <div className={`w-full ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded-full h-2`}>
                  <div 
                    className="bg-teal-600 h-2 rounded-full" 
                    style={{ width: `${(parrotDetails?.configuration?.voice_settings?.similarity_boost || 0.5) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Style: {editing ? editForm.voice_settings.style : parrotDetails?.configuration?.voice_settings?.style || 0.0}
              </label>
              {editing ? (
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={editForm.voice_settings.style}
                  onChange={(e) => handleInputChange('voice_settings.style', parseFloat(e.target.value))}
                  className="w-full"
                />
              ) : (
                <div className={`w-full ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded-full h-2`}>
                  <div 
                    className="bg-teal-600 h-2 rounded-full" 
                    style={{ width: `${(parrotDetails?.configuration?.voice_settings?.style || 0.0) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Associated Resources */}
        <div>
          <h4 className={`text-md font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Associated Resources</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Phone Numbers ({editing ? editForm.phone_numbers?.length || 0 : parrotDetails?.phone_numbers?.length || 0})
              </label>
              {editing ? (
                <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} rounded-md p-3 max-h-48 overflow-y-auto`}>
                  {loadingPhoneNumbers ? (
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Loading phone numbers...</p>
                  ) : availablePhoneNumbers.length > 0 ? (
                    <div className="space-y-2">
                      {availablePhoneNumbers.map((phone) => (
                        <label key={phone.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.phone_numbers.includes(phone.id)}
                            onChange={(e) => handlePhoneNumberChange(phone.id, e.target.checked)}
                            className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded dark:border-gray-600"
                          />
                          <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {phone.phone_number} - {phone.friendlyName || 'No name'}
                            {phone.parrot_id && phone.parrot_id !== parrot.id && (
                              <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                (Currently assigned to another parrot)
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm`}>No phone numbers available</p>
                  )}
                </div>
              ) : (
                <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} rounded-md p-3 max-h-32 overflow-y-auto`}>
                  {parrotDetails?.phone_numbers?.length > 0 ? (
                    <ul className="space-y-1">
                      {parrotDetails.phone_numbers.map((phone, index) => (
                        <li key={index} className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {phone.phone_number} - {phone.friendly_name || 'No name'}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm`}>No phone numbers assigned</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Knowledge Base Files ({parrotDetails?.knowledge_base_files?.length || 0})
              </label>
              <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} rounded-md p-3 max-h-32 overflow-y-auto`}>
                {parrotDetails?.knowledge_base_files?.length > 0 ? (
                  <ul className="space-y-1">
                    {parrotDetails.knowledge_base_files.map((file, index) => (
                      <li key={index} className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {file.name} ({file.file_size ? `${Math.round(file.file_size / 1024)}KB` : 'Unknown size'})
                        {file.sync_status && (
                          <span className={`ml-2 text-xs px-1 py-0.5 rounded ${
                            file.sync_status === 'synced'
                              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                              : file.sync_status === 'failed'
                              ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                          }`}>
                            {file.sync_status}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm`}>No Knowledge Base files uploaded</p>
                )}
              </div>
            </div>
          </div>
        </div>

  
        {/* Knowledge Base Sync Status */}
        <div>
          <h4 className={`text-md font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Knowledge Base Status</h4>
          <KnowledgeBaseSyncStatusPanel
            parrotId={parrot.id}
            parrotName={parrotDetails?.name || parrot.name}
            showHeader={false}
            expandedByDefault={true}
            autoRefresh={true}
            refreshInterval={30000}
          />
        </div>

        {/* Metadata */}
        <div>
          <h4 className={`text-md font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>Metadata</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Created At
              </label>
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{formatDate(parrotDetails?.created_at)}</p>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Last Updated
              </label>
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{formatDate(parrotDetails?.updated_at)}</p>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                Revision Count
              </label>
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{parrotDetails?.revision_count || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      <ParrotAssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        parrot={parrotDetails}
        onAssignmentComplete={(updatedParrot) => {
          setParrotDetails(updatedParrot);
          onUpdate(updatedParrot);
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-md w-full ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Delete Parrot Permanently
                </h2>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className={`transition-colors ${
                    theme === 'dark' 
                      ? 'text-gray-400 hover:text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  } disabled:opacity-50`}
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className={`mb-4 p-4 rounded-lg ${
                theme === 'dark' 
                  ? 'bg-red-900/20 border border-red-500/30' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start space-x-3">
                  <span className="text-red-500 text-xl">⚠️</span>
                  <div>
                    <h3 className={`font-medium ${theme === 'dark' ? 'text-red-400' : 'text-red-700'} mb-2`}>
                      This action cannot be undone
                    </h3>
                    <p className={`text-sm ${theme === 'dark' ? 'text-red-300' : 'text-red-600'}`}>
                      This will permanently delete the parrot "{parrotDetails?.name}" and all associated data including:
                    </p>
                    <ul className={`text-sm ${theme === 'dark' ? 'text-red-300' : 'text-red-600'} mt-2 ml-4 list-disc`}>
                      <li>All configuration revisions</li>
                      <li>Phone number assignments</li>
                      <li>RAG file associations</li>
                      <li>ElevenLabs agent (if exists)</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                Are you sure you want to permanently delete this parrot?
              </p>
            </div>
            
            <div className={`px-6 py-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex justify-end space-x-3`}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className={`px-6 py-2 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteParrot}
                disabled={deleting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2 dark:text-white"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <span>🗑️</span>
                    <span>Delete Permanently</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParrotDetailsPanel;
